import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { ArrowRight } from "lucide-react";
import { ExternalLink } from "lucide-react";

const HomeSection = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });

    const scrollContainer = scrollRef.current;
    let scrollAmount = 0;
    const scrollStep = 0.5;
    const scrollInterval = 16;

    const interval = setInterval(() => {
      if (scrollContainer) {
        scrollAmount += scrollStep;
        if (scrollAmount >= scrollContainer.scrollWidth / 2) {
          scrollAmount = 0; // seamless loop
        }
        scrollContainer.scrollLeft = scrollAmount;
      }
    }, scrollInterval);

    return () => clearInterval(interval);
  }, []);

  const services = [
    {
      title: "Deployment & Configuration",
      description:
        "We set up iSAMS tailored to your school’s structure, ensuring optimal workflows from day one.",
      imageSrc: "/images/Dep & conf.png",
      link: "/deployment-configuration",
    },
    {
      title: "Data Cleaning & Migration",
      description:
        "Seamless and secure transfer of legacy data into iSAMS, with thorough accuracy checks and cleanup.",
      imageSrc: "/images/DC and Migration.png",
      link: "/data-cleaning-migration",
    },
    {
      title: "Staff Training & Onboarding",
      description:
        "Hands-on training sessions to ensure your team is confident and capable using every iSAMS module.",
      imageSrc: "/images/ST and SO.png",
      link: "/staff-training-onboarding",
    },
    {
      title: "API Integration & Automation",
      description:
        "We help build custom reports and handle API integrations to ensure smooth data flow and actionable insights.",
      imageSrc: "/images/API I and Aut.png",
      link: "/api-integration-automation",
    },
    {
      title: "Maintenance & Technical Support",
      description:
        "Continuous tech support, updates, and technical troubleshooting to keep your iSAMS system running smoothly.",
      imageSrc: "/images/M and TS.png",
      link: "/maintenance-support",
    },
    {
      title: "Data Management & Optimization",
      description:
        "We clean, validate, and organize academic and administrative data for seamless MIS & LMS operations.",
      imageSrc: "/images/DM and DO.png",
      link: "/data-management",
    },
    {
      title: "Hiring, Payroll & HR Operations",
      description:
        "We help schools hire skilled teachers & admin staff on payroll, simplifying recruitment & operations.",
      imageSrc: "/images/Hir,Pay and HR.png",
      link: "/hiring-payrolls",
    },
  ];

  const steps = [
    {
      number: "01.",
      title: "Free Consultation",
      description:
        "Connect with our team to schedule a free consultation and explore how our School MIS, LMS, or Timetabling solutions can fit your needs.",
    },
    {
      number: "02.",
      title: "Setup & Customization",
      description:
        "We configure and customize platforms like iSAMS, Canvas, and ASC Software to match your needs, ensuring smooth migration.",
    },
    {
      number: "03.",
      title: "Training & Go-Live",
      description:
        "Our team trains your staff and delivers a seamless launch with complete post-implementation assistance and ongoing technical support.",
    },
  ];

  const testimonials = [
    {
      name: "John Doe",
      role: "School Principal",
      feedback:
        "EduLynks transformed how we manage our school. The dashboards are intuitive and save us so much time!",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      name: "Jane Smith",
      role: "IT Manager",
      feedback:
        "The MIS and LMS solutions are seamless. Our staff adapted quickly thanks to EduLynks' training.",
      image: "https://randomuser.me/api/portraits/women/44.jpg",
    },
    {
      name: "Michael Lee",
      role: "Teacher",
      feedback:
        "Timetabling has never been easier. EduLynks helped us go digital efficiently and painlessly.",
      image: "https://randomuser.me/api/portraits/men/54.jpg",
    },
    {
      name: "Sara Khan",
      role: "Administrator",
      feedback:
        "Excellent support and easy-to-use dashboards. The team ensured a smooth migration for our school.",
      image: "https://randomuser.me/api/portraits/women/65.jpg",
    },
  ];

  const scrollingTestimonials = [...testimonials, ...testimonials];

  const images = [
    "https://media.licdn.com/dms/image/v2/D5612AQEl5oIja1BDAg/article-cover_image-shrink_720_1280/article-cover_image-shrink_720_1280/0/1695535071929?e=2147483647&v=beta&t=I6OjWre2m9P-muDyqLZGctQCaphz9_G5El7ym0kP2sE",
    "https://img.freepik.com/free-vector/user-panel-template-infographic-dashboard_23-2148378206.jpg",
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTLgKoiJ_zT_mYkrpCTbP26L4cp46MLrU7X_2L9KDrhVhmcP6BUAo3NHbah8at8zqWCrGI&usqp=CAU",
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTRPpSdhiCD_hqptSunKU5l4mNLToMev8N4sQ&s",
    "https://media.istockphoto.com/id/1488294044/photo/businessman-works-on-laptop-showing-business-analytics-dashboard-with-charts-metrics-and-kpi.jpg?s=612x612&w=0&k=20&c=AcxzQAe1LY4lGp0C6EQ6reI7ZkFC2ftS09yw_3BVkpk=",
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQnCcoHWsz4ZqsdiKexEQJqG1txMSGJMPUlsuiz2xbbaPC_Z24oU-f1_MqPVQOPlU90mpg&usqp=CAU",
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRSeaiJF23EyBdG5-d7EQ5JwapUKZckQoxjuBfQkbwCmiagFCPdaogcTPV7gsYcqBc1ueE&usqp=CAU",
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSfi-urCbko9bv3wp4ga-eOlZOE9N8jQ780xA&s",
  ];

  return (
    <>
      <section className="px-[5%]">
        {/* <!-- Hero Section --> */}
        <div className="max-w-7xl mx-auto bg-white grid grid-cols-1 lg:grid-cols-2 gap-6 items-center py-2">
          {/* <!-- Text Column --> */}
          <div className="">
            <p className="text-lg text-gray-700">Welcome to EduLynks !</p>
            <h2 className="text-2xl md:text-4xl font-bold mt-2 mb-3 leading-snug md:leading-relaxed bg-gradient-to-r from-[#0d6b68] to-[#0d6b68] bg-clip-text text-transparent">
              Empowering Education Through Smart Technology
            </h2>

            <h3 className="text-lg text-gray-500 mt-4">
              Edulynks transforms how schools and universities operate with
              intelligent dashboards that simplify management, enhance learning,
              and optimize time. From MIS and LMS to AI-powered timetabling, we
              deliver tailored solutions from setup to analytics built for
              modern education.
            </h3>
            <div className="flex flex-wrap gap-4 mt-4">
              <a
                href="tel:+917994670962"
                className="btn1 group inline-flex items-center gap-3"
              >
                <span className="font-medium transition">
                  Start Conversation
                </span>
                <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-1 duration-200" />
              </a>
            </div>
            <p className="text-lg text-black font-medium mt-6 inline-block relative pb-1">
              Available on Social Media
              <span className="absolute left-0 bottom-0 w-full h-1 bg-gradient-to-r from-[#2b9995] to-[#0d6b68] rounded-full"></span>
            </p>
          </div>
          <div className="relative group p-2 rounded-full bg-gradient-to-br from-[#0d6b68] via-white to-gray-100">
            <img
              src="./images/student.png"
              alt="Student"
              className="w-full h-full object-contain rounded-full border-[6px] border-white"
            />
            {/* Button appears on hover */}
            <a className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <a
                href="tel:+917994670962"
                className="btn1 group inline-flex items-center gap-3"
              >
                <span className="font-medium transition">Get in Touch</span>
              </a>
            </a>
          </div>
        </div>
      </section>

      <section className="px-[5%]">
        {/* Single Row Section: Trust + Reviews */}
        <div
          data-aos="fade-right"
          className="max-w-7xl mx-auto my-14 flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left"
        >
          {/* Trust Text */}
          <div>
            <p className="font-semibold text-xl">250K+ students</p>
            <p className="text-sm text-gray-600">trusted our products</p>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px h-10 bg-gray-800"></div>

          {/* Reviews.io */}
          <div className="flex items-center gap-3">
            <div className="bg-gray-100 p-2 rounded-full">
              <img
                src="https://asset.edubirdie.com/bundles/asterfreelance/_layout/images/EdubirdieCom/mars/rating/Reviews.io.svg?1753356716"
                alt="Reviews.io"
                width="24"
                height="24"
              />
            </div>
            <div>
              <p className="font-semibold text-xl">EDTECH Expertise</p>
              <p className="text-sm text-gray-600">
                10+ Years in School Systems
              </p>
            </div>
          </div>
          {/* Divider */}
          <div className="hidden md:block w-px h-10 bg-gray-800"></div>
          {/* Sitejabber */}
          <div className="flex items-center gap-3">
            <div className="bg-gray-100 p-2 rounded-full">
              <img
                src="https://asset.edubirdie.com/bundles/asterfreelance/_layout/images/EdubirdieCom/mars/rating/Sitejabber.svg?1753356716"
                alt="Sitejabber"
                width="24"
                height="24"
              />
            </div>
            <div>
              <p className="font-semibold text-xl">Custom Implementation</p>
              <p className="text-sm text-gray-600">
                Tailored Module Configuration
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-[5%]">
        {/* <!-- Hero Section --> */}
        <div className="max-w-7xl mx-auto my-14 bg-[#0d6b68] grid grid-cols-1 lg:grid-cols-2 gap-6 items-center p-6 rounded-[40px]">
          {/* <!-- Text Column --> */}
          <div className="">
            <p className="text-4xl font-bold text-gray-900">
              EduLynks for Students
            </p>
            <h2 className="text-2xl md:text-4xl font-bold mt-2 mb-3 text-white">
              Empowering Education Through Smart Technology
            </h2>

            <h3 className="text-lg text-white mt-4">
              Edulynks transforms how schools and universities operate with
              intelligent dashboards that simplify management, enhance learning,
              and optimize time. From MIS and LMS to AI-powered timetabling, we
              deliver tailored solutions from setup to analytics built for
              modern education.
            </h3>
            <div className="flex flex-wrap gap-4 mt-4">
              <a
                href="tel:+917994670962"
                className="btn2 group inline-flex items-center gap-3"
              >
                <span className="font-medium transition">Try EduLynks</span>
              </a>
            </div>
          </div>
          <div className="relative group p-2 rounded-full">
            <img
              src="./images/student.png"
              alt="Student"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      </section>

      <section className="px-[5%]">
        <div
          className="max-w-7xl mx-auto min-h-screen bg-white py-16"
          id="services"
        >
          <div
            className="text-center max-w-4xl mx-auto mb-10"
            data-aos="fade-up"
          >
            <h1 className="threeD-text-black text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Services That Move You{" "}
              <span className="text-[#0d6b68]">Forward</span>
            </h1>
            <p className="text-xs md:text-base text-gray-900 max-w-2xl mx-auto leading-relaxed">
              We Provide End-To-End Services for Schools, Ensuring High
              Performance, Seamless User Experience, And Growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                onClick={() => navigate(service.link)}
                key={index}
                className="bg-gradient-to-t from-white to-gray-300 border border-gray-200 rounded-3xl p-5 shadow-md hover:shadow-xl transition duration-300 group cursor-pointer"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <img
                  src={service.imageSrc}
                  alt={service.title}
                  className="w-full h-40 object-cover border-l-4 border-r-4 border-gray-800 rounded-xl mb-6 group-hover:scale-105 transition-transform duration-500"
                />

                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/84/84590.png"
                      className="transform rotate-[90deg]"
                      alt="icon"
                    />
                  </div>
                  <h3 className="text-sm sm:text-lg lg:text-2xl font-bold text-[#0d6b68]">
                    {service.title}
                  </h3>
                </div>

                <p className="text-gray-600 text-sm md:text-base mb-6 leading-relaxed">
                  {service.description}
                </p>

                <button
                  onClick={() => navigate(service.link)}
                  className="inline-flex text-sm items-center space-x-2 font-medium transition duration-300 rounded-full border border-gray-400 p-1 pl-3 text-white bg-gradient-to-r from-black to-gray-500 hover:border-white m-[8px] hover:from-gray-500 hover:to-black"
                >
                  <span>Learn More</span>
                  <ArrowRight
                    style={{
                      padding: "0.5rem",
                      width: "2rem",
                      height: "2rem",
                      borderRadius: "9999px",
                      backgroundColor: "#2b9995",
                      color: "black",
                      transform: "rotate(-45deg)",
                      animation: "zoomPulse 1.5s ease-in-out infinite",
                    }}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-[5%]">
        <div className="max-w-7xl mx-auto">
          <h1
            className="text-3xl md:text-4xl font-bold text-gray-900 text-center p-2 md:text-center mb-10"
            data-aos="fade-up"
          >
            EdTech Experts <span className="text-[#0d6b68]">Global Reach.</span>
          </h1>

          {/* Trust Items Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 justify-items-center">
            {/* Trust Item */}
            <div className="relative bg-gray-200 drop-animate w-28 h-28 flex flex-col items-center justify-center rounded-[50%_50%_60%_40%/60%_40%_60%_50%] shadow-lg hover:shadow-2xl transition-all duration-300">
              <p className="font-semibold text-2xl text-[#0d6b68]">8+</p>
              <p className="text-lg text-gray-600">Projects</p>
            </div>

            <div className="relative bg-gray-200 drop-animate w-28 h-28 flex flex-col items-center justify-center rounded-[60%_40%_50%_50%/50%_60%_40%_50%] shadow-lg hover:shadow-2xl transition-all duration-300">
              <p className="font-semibold text-2xl text-[#0d6b68]">320+</p>
              <p className="text-lg text-gray-600">Users</p>
            </div>

            <div className="relative bg-gray-200 drop-animate w-28 h-28 flex flex-col items-center justify-center rounded-[55%_45%_50%_50%/50%_55%_45%_50%] shadow-lg hover:shadow-2xl transition-all duration-300">
              <p className="font-semibold text-2xl text-[#0d6b68]">8+</p>
              <p className="text-lg text-gray-600">Years</p>
            </div>

            <div className="relative bg-gray-200 drop-animate w-28 h-28 flex flex-col items-center justify-center rounded-[50%_60%_40%_50%/60%_40%_60%_50%] shadow-lg hover:shadow-2xl transition-all duration-300">
              <p className="font-semibold text-2xl text-[#0d6b68]">5+</p>
              <p className="text-lg text-gray-600">Countries</p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-[5%]">
        <div className="max-w-7xl mx-auto my-16 bg-white mx-auto text-center">
          <h2
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 py-10"
            data-aos="fade-up"
          >
            Simple 3 Step <span className="text-[#0d6b68]">Process</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {steps.map((step, index) => (
              <div
                key={index}
                className="bg-[#0d6b68]/20 border-l-4 border-[#0d6b68] rounded-[50px] p-6 text-left relative shadow-lg hover:shadow-2xl transition-shadow duration-500"
                data-aos="fade-up"
                data-aos-delay={index * 200}
              >
                <div className="text-3xl font-bold text-white absolute -top-6 left-6 bg-[#0d6b68] p-2 rounded-full">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-black mb-3 mt-4">
                  {step.title}
                </h3>
                <p className="text-gray-800">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-[5%]">
        <div className="max-w-7xl mx-auto">
          {/* <!-- Hero Section --> */}
          <h2
            className="text-3xl md:text-4xl font-bold text-gray-800 mb-12 text-center"
            data-aos="fade-up"
          >
            Our <span className="text-[#0d6b68]">Product</span>
          </h2>
          <div className="bg-white grid grid-cols-1 lg:grid-cols-2 gap-6 items-center py-2">
            {/* <!-- Text Column --> */}
            <div className="">
              <h2 className="text-2xl md:text-4xl font-bold mt-2 mb-3 leading-snug md:leading-relaxed bg-gradient-to-r from-[#0d6b68] to-[#0d6b68] bg-clip-text text-transparent">
                Vetri Dashboard
              </h2>

              <h3 className="text-lg text-gray-500 mt-4">
                Vetri Dashboard is an all-in-one platform designed to simplify
                school and university operations. From student and staff
                management to HR, attendance, exams, and analytics everything is
                organized in one intelligent dashboard to enhance productivity
                and decision-making.
              </h3>
              <div className="flex flex-wrap gap-4 mt-4">
                <button
                  onClick={() => navigate("/our-product-vetri")}
                  className="inline-block btn1"
                >
                  Try Vetri Dashboard
                </button>
              </div>
            </div>
            <div className="relative bg-black flex justify-center items-center overflow-hidden rounded-2xl p-4 h-[300px]">
              <div className="scope w-[240px] h-[140px]">
                {images.map((img, i) => (
                  <span key={i} style={{ "--i": i + 1 }}>
                    <img
                      src={img}
                      alt={`gallery-${i}`}
                      className="rounded-xl"
                      loading="lazy"
                    />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-[5%]">
        <div className="max-w-7xl mx-auto my-16 mx-auto bg-gray-900 p-8 rounded-3xl">
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-12 text-center"
            data-aos="fade-up"
          >
            Client Testimonials
          </h2>

          <div ref={scrollRef} className="flex gap-8 overflow-x-hidden">
            {scrollingTestimonials.map((t, index) => (
              <div
                key={index}
                className="min-w-[280px] md:min-w-[320px] bg-white text-black rounded-3xl p-4 duration-500 flex flex-col items-center gap-4"
                data-aos="fade-up"
                data-aos-delay={(index % testimonials.length) * 150}
              >
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-4 border-[#0d6b68] shadow-lg">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-center text-gray-800 text-sm md:text-base font-medium">
                  {t.feedback}
                </p>
                <h4 className="font-bold text-[#0d6b68] text-lg">{t.name}</h4>
                <p className="text-gray-600 text-sm">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default HomeSection;
